import {
    categorise,
    FINAL_CATEGORIES,
    UNSETTLED_CATEGORIES,
    type FinalCategory,
} from './final-category';
import { isActive } from './job-progress';
import { normaliseFailure, normaliseIssues, type ReportIssue } from './report-issues';

import type { BulkModule, JobHeader, RecordRow, TenantRow } from '../schemas/bulk-migration';

/**
 * The final report of a bulk migration: the audit an operator shares once it
 * is over, built from the whole job chain rather than from one job.
 *
 * One row per client the analysis looked at — plus any client a later job
 * names that the analysis does not, so a client can never fall out of the
 * report because its rows disagree. Each row keeps the module's RAW values from
 * the analysis (why it was or was not migrated) and, when it was launched, each
 * record's tracking (what it became in V2).
 */

export interface FinalRecordTracking {
    readonly status: string;
    readonly attempts: number;
    readonly v2Id: string | null;
    readonly migratedAt: string | null;
    readonly warnings: readonly ReportIssue[];
    readonly failure: ReportIssue | null;
}

export interface FinalRecord {
    readonly sourceId: string;
    readonly title: string;
    /** The migrator's raw values, as the analysis read them in Salesforce. */
    readonly columns: Readonly<Record<string, unknown>>;
    readonly notMigrated: { readonly code: string; readonly message: string } | null;
    /** The module's own preview of the record (Branding: the logo probe), unread here. */
    readonly preview: unknown;
    /** `null` until a refresh has read the record's tracking row. */
    readonly tracking: FinalRecordTracking | null;
}

/** The client's run as the last report read it. */
export interface FinalRun {
    readonly runId: string | null;
    /** `Completed`, `Running`… — `null` before any refresh read the run. */
    readonly status: string | null;
    /** `discovered`, `migrated`, `failed`, `skipped`… — `null` when nobody read them. */
    readonly totals: Readonly<Record<string, number>> | null;
    readonly startedAt: string | null;
    readonly finishedAt: string | null;
}

export interface FinalReportRow {
    readonly tenant: {
        readonly tenantId: string;
        readonly name: string;
        readonly slug: string | null;
        readonly orgId: string | null;
        readonly isSandbox: boolean;
        /** The tenant's status in the catalogue (`active`…). */
        readonly status: string;
    };
    readonly category: FinalCategory;
    readonly reason: string | null;
    readonly message: string | null;
    readonly launched: boolean;
    readonly run: FinalRun | null;
    readonly records: readonly FinalRecord[];
    /** How many records the analysis found; `records` holds at most the runner's 100. */
    readonly recordsTotal: number;
}

export interface JobSummary {
    readonly reportId: string;
    readonly state: JobHeader['state'];
    readonly requestedBy: string;
    readonly reason: string | null;
    readonly createdAt: string;
    readonly finishedAt: string | null;
    readonly excludeSandboxes: boolean;
    /** Where the runner exported the job's consolidated JSON, once it did. */
    readonly exportLocation: string | null;
}

export interface FinalReportHeader {
    readonly moduleId: string;
    readonly moduleTitle: string;
    readonly environment: string | null;
    readonly dryRun: JobSummary | null;
    readonly launch: JobSummary;
    readonly lastRefresh: JobSummary | null;
    readonly generatedAt: string;
    /** Nothing left running or unread, and the launch itself finished. */
    readonly isFinal: boolean;
    readonly unsettled: number;
    /**
     * The refresh was looked up in a window of the module's newest jobs that
     * may not reach back to it: "never refreshed" is then not a fact.
     */
    readonly refreshLookupLimited: boolean;
}

export interface FinalReport {
    readonly header: FinalReportHeader;
    readonly counts: Readonly<Record<FinalCategory, number>>;
    readonly rows: readonly FinalReportRow[];
}

export interface FinalReportInput {
    readonly module: Pick<BulkModule, 'moduleId' | 'title'>;
    readonly environment?: string | null;
    readonly dryRun: JobHeader | null;
    readonly dryRunRows: readonly TenantRow[];
    readonly launch: JobHeader;
    readonly launchRows: readonly TenantRow[];
    /** The newest FINISHED refresh of the launch, or `null` when none has run. */
    readonly refresh: JobHeader | null;
    readonly refreshRows: readonly TenantRow[];
    /** See {@link FinalReportHeader.refreshLookupLimited}; `false` when omitted. */
    readonly refreshLookupLimited?: boolean;
    readonly now?: Date;
}

function summary(job: JobHeader): JobSummary {
    return {
        reportId: job.reportId,
        state: job.state,
        requestedBy: job.requestedBy,
        reason: job.reason,
        createdAt: job.createdAt,
        finishedAt: job.finishedAt,
        excludeSandboxes: job.excludeSandboxes,
        exportLocation: job.exportLocation,
    };
}

function byTenant(rows: readonly TenantRow[]): Map<string, TenantRow> {
    return new Map(rows.map((row) => [row.tenant.tenantId, row]));
}

function trackingOf(record: RecordRow | undefined): FinalRecordTracking | null {
    const tracking = record?.tracking;
    if (tracking === undefined) return null;
    return {
        status: tracking.status,
        attempts: tracking.attempts,
        v2Id: tracking.v2Id,
        migratedAt: tracking.migratedAt,
        warnings: normaliseIssues(tracking.warnings),
        failure: normaliseFailure(tracking.failure),
    };
}

function finalRecord(analysed: RecordRow, latest: RecordRow | undefined): FinalRecord {
    return {
        sourceId: analysed.sourceId,
        title: analysed.title,
        columns: analysed.columns,
        notMigrated: analysed.notMigrated,
        preview: latest?.preview ?? analysed.preview ?? null,
        tracking: trackingOf(latest),
    };
}

/**
 * The analysis' records, in its order, each with the tracking the latest row
 * carries for it; then any record only the latest row knows.
 */
export function mergeRecords(
    analysed: TenantRow | undefined,
    latest: TenantRow | undefined,
): FinalRecord[] {
    const base = analysed?.records ?? [];
    const tracked = new Map((latest?.records ?? []).map((record) => [record.sourceId, record]));
    const known = new Set(base.map((record) => record.sourceId));
    return [
        ...base.map((record) => finalRecord(record, tracked.get(record.sourceId))),
        ...(latest?.records ?? [])
            .filter((record) => !known.has(record.sourceId))
            .map((record) => finalRecord(record, record)),
    ];
}

function runOf(row: TenantRow | undefined): FinalRun | null {
    const run = row?.run;
    if (run === undefined) return null;
    return {
        runId: run.runId,
        status: run.status ?? null,
        totals: run.totals ?? null,
        startedAt: run.startedAt ?? null,
        finishedAt: run.finishedAt ?? null,
    };
}

/**
 * Who the client is: the newest row that knows it. The runner names a tenant
 * it cannot find with a stub (`status: 'unknown'`, no name), which must never
 * overwrite what an earlier job knew.
 */
function tenantOf(rows: readonly (TenantRow | undefined)[]): TenantRow['tenant'] {
    const present = rows.filter((row): row is TenantRow => row !== undefined);
    // `present` is not empty: the row is only built for a tenant some job names.
    const known = present.find((row) => row.tenant.status !== 'unknown') ?? present[0];
    return (known as TenantRow).tenant;
}

function reportRow(
    dryRun: TenantRow | undefined,
    launch: TenantRow | undefined,
    refresh: TenantRow | undefined,
): FinalReportRow {
    const tenant = tenantOf([refresh, launch, dryRun]);
    const verdict = categorise({ dryRun, launch, refresh });
    const records = mergeRecords(dryRun, refresh ?? launch);
    return {
        tenant: {
            tenantId: tenant.tenantId,
            name: tenant.displayName.trim() === '' ? tenant.tenantId : tenant.displayName,
            slug: tenant.slug,
            orgId: tenant.orgId,
            isSandbox: tenant.isSandbox,
            status: tenant.status,
        },
        ...verdict,
        launched: launch !== undefined,
        // A refresh that failed to read the run carries none: keep the launch's.
        run: runOf(refresh?.run === undefined ? launch : refresh),
        records,
        recordsTotal: Math.max(
            dryRun?.recordsTotal ?? 0,
            launch?.recordsTotal ?? 0,
            records.length,
        ),
    };
}

const CATEGORY_ORDER = new Map(FINAL_CATEGORIES.map((category, index) => [category, index]));

function emptyCounts(): Record<FinalCategory, number> {
    return Object.fromEntries(FINAL_CATEGORIES.map((category) => [category, 0])) as Record<
        FinalCategory,
        number
    >;
}

export function buildFinalReport(input: FinalReportInput): FinalReport {
    const dryRuns = byTenant(input.dryRunRows);
    const launches = byTenant(input.launchRows);
    const refreshes = byTenant(input.refresh === null ? [] : input.refreshRows);
    const tenantIds = new Set([...dryRuns.keys(), ...launches.keys(), ...refreshes.keys()]);

    const rows = [...tenantIds]
        .map((id) => reportRow(dryRuns.get(id), launches.get(id), refreshes.get(id)))
        .sort(
            (a, b) =>
                (CATEGORY_ORDER.get(a.category) ?? 0) - (CATEGORY_ORDER.get(b.category) ?? 0) ||
                a.tenant.name.localeCompare(b.tenant.name),
        );

    const counts = emptyCounts();
    for (const row of rows) counts[row.category] += 1;
    const unsettled = rows.filter((row) => UNSETTLED_CATEGORIES.has(row.category)).length;

    return {
        header: {
            moduleId: input.module.moduleId,
            moduleTitle: input.module.title,
            environment: input.environment ?? null,
            dryRun: input.dryRun === null ? null : summary(input.dryRun),
            launch: summary(input.launch),
            lastRefresh: input.refresh === null ? null : summary(input.refresh),
            generatedAt: (input.now ?? new Date()).toISOString(),
            isFinal: unsettled === 0 && !isActive(input.launch.state),
            unsettled,
            refreshLookupLimited: input.refreshLookupLimited === true && input.refresh === null,
        },
        counts,
        rows,
    };
}
